//  @ts-nocheck
import axios from "axios";
import jwtDecode from "jwt-decode";
import { USER_LOGIN_SUCCESS } from "../../redux/constants/userConstants";
import store from "../../redux/store";

// creates and axios instance with base url
const axiosInstance = axios.create({
  baseURL: `${process.env.REACT_APP_API_ENDPOINT_LEADS}`,
});

let isRefreshing = false;
let refreshSubscribers: any[] = [];

const subscribeTokenRefresh = (cb: any) => {
  refreshSubscribers.push(cb);
};

const onRefreshed = (token: string) => {
  refreshSubscribers.map((cb) => cb(token));
};

axiosInstance.interceptors.request.use(
  async (config?: any) => {
    // sets the content type to json
    config.headers["Content-Type"] = "application/json";
    return config;
  },
  (error) => {
    console.error("Error while setting up axios request interceptor,", error);
  }
);

// Add a response interceptor which checks for error code for all the requests
axiosInstance.interceptors.response.use(
  (response) => response,
  async (err) => {
    const originalRequest = err.config;

    if (
      (err?.response?.status === 403 || err?.response?.status === 401) &&
      err?.response?.data === "Token Invalidation" &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true;

      if (!isRefreshing) {
        isRefreshing = true;
        try {
          const refreshResponse = await axios.post(
            `${process.env.REACT_APP_API_ENDPOINT_LEADS}/user/refresh-token`,
            {
              rToken: localStorage.getItem("refreshToken"),
            },
            { withCredentials: true }
          );

          const newAccessToken = refreshResponse.data.accessToken;
          const newRefreshToken = localStorage.getItem("refreshToken");
          localStorage.setItem("accessToken", newAccessToken);
          localStorage.setItem("refreshToken", newRefreshToken);
          store.dispatch({
            type: USER_LOGIN_SUCCESS,
            payload: {
              ...refreshResponse.data.savedUser,
              accessToken: newAccessToken,
              refreshToken: newRefreshToken,
            },
          });

          store.dispatch({
            type: USER_LOGIN_SUCCESS,
            payload: {
              ...refreshResponse.data.savedUser,
              accessToken: newAccessToken,
            },
          });

          onRefreshed(newAccessToken);

          originalRequest.headers["Authorization"] = `Bearer ${newAccessToken}`;
          localStorage.setItem("refreshToken", refreshToken);
          return axiosInstance(originalRequest);
        } catch (refreshError) {
          console.error("Refresh token failed during response:", refreshError);

          // Clear everything on hard failure
          localStorage.removeItem("accessToken");
          localStorage.removeItem("refreshToken");
          localStorage.removeItem("userInfo");
          localStorage.setItem("redirectPath", window.location.href);
          window.location.href = "/login";
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
          refreshSubscribers = [];
        }
      } else {
        // Refresh is already happening
        try {
          await new Promise((resolveSub) => {
            subscribeTokenRefresh((newAccessToken: string) => {
              originalRequest.headers[
                "Authorization"
              ] = `Bearer ${newAccessToken}`;
              resolveSub(null);
            });
          });

          return axiosInstance(originalRequest);
        } catch (err) {
          return Promise.reject(err);
        }
      }
    }

    console.error("Request failed:", err?.response?.data || err.message);
    return Promise.reject(err);
  }
);

interface RequestParams {
  url: string;
  method?: string;
  isAuthenticated?: boolean;
  data?: any;
  header?: object;
}

const request = async ({
  url,
  method = "GET",
  isAuthenticated = false,
  data = undefined,
  header = {},
  action = "",
  notes = "",
}: RequestParams) => {
  return new Promise(async (resolve, reject) => {
    let options: any = {
      method,
      url,
    };

    const state = store.getState();
    let userInfo = state.userLogin?.userInfo;
    const currentTabPermissions = state.currentTabPermissions;
    let accessToken = userInfo?.accessToken;

    if (isAuthenticated && !accessToken) {
      if (!isRefreshing) {
        isRefreshing = true;
        try {
          const refreshResponse = await axios.post(
            `${process.env.REACT_APP_API_ENDPOINT_LEADS}/user/refresh-token`,
            {
              rToken: localStorage.getItem("refreshToken"),
            },
            { withCredentials: true }
          );

          const newAccessToken = refreshResponse.data.accessToken;
          const refreshToken = refreshResponse.data.refreshToken;
          store.dispatch({
            type: USER_LOGIN_SUCCESS,
            payload: {
              ...refreshResponse.data.savedUser,
              accessToken: newAccessToken,
            },
          });

          accessToken = newAccessToken;
          localStorage.setItem("refreshToken", refreshToken);
          onRefreshed(newAccessToken);
        } catch (refreshError) {
          console.error("Refresh token failed before request:", refreshError);
          localStorage.setItem("redirectPath", window.location.href);
          window.location.href = "/login";
          return reject(refreshError);
        } finally {
          isRefreshing = false;
          refreshSubscribers = [];
        }
      } else {
        await new Promise((resolveSub) => {
          subscribeTokenRefresh((newAccessToken: string) => {
            accessToken = newAccessToken;
            resolveSub(null);
          });
        });
      }
    }

    if (isAuthenticated) {
      // const accessToken = userInfo?.accessToken;
      const accessToken = localStorage.getItem("accessToken");
      // const accessToken = localStorage.getItem("");

      if (!accessToken) {
        // return new Error("Access token is not available.");
        // alert("Please Login!! can't find accessToken 110");
        // window.location.pathname = "/login";
      }
      // adds Authorization to headers in options
      options.headers = {
        Authorization: `Bearer ${accessToken}`,
        action: action,
        tab: currentTabPermissions?.tab,
        permissionid: currentTabPermissions?.permissionsId,
        eventid: currentTabPermissions?.permissionsEventId,
        notes: notes,
        ...header,
      };
    }

    if (["POST", "PUT", "PATCH", "DELETE"].includes(method) && data) {
      options.data = data;
    }

    axiosInstance(options).then(resolve).catch(reject);
  }).catch((err) => {
    console.error(`Error while making ${method} request:`, {
      url,
      isAuthenticated,
      err,
    });
    return Promise.reject(err);
  });
};

export const getCurrentUnixTimestamp = () => Math.floor(Date.now() / 1000);

export const getExpirationTimeStamp = (token: string) => {
  const decodedToken = jwtDecode(token);

  const { exp } = decodedToken;
  return exp;
};

// calls the [request] function with [url]
export const getRequest = (url: string, header: object = {}) =>
  request({ url, header });

// calls the [request] function with [url] and [isAuthenticated = true]
export const getAuthenticatedRequest = (url: string, header: object = {}) =>
  request({ url, isAuthenticated: true, header });

// calls the [request] function with [url], [data], [method = 'POST'] and [isAuthenticated = false]
export const postRequest = (url: string, data: any, header: object = {}) =>
  request({ url, method: "POST", isAuthenticated: false, data, header });

// calls the [request] function with [url], [data], [method = 'POST'] and [isAuthenticated = true]
export const postAuthenticatedRequest = (
  url: string,
  data: any,
  header: object = {},
  notes: string = ""
) =>
  request({
    url,
    method: "POST",
    isAuthenticated: true,
    data,
    header,
    action: "edit",
    notes: notes,
  });

// calls the [request] function with [url], [data], [method = 'PUT'] and [isAuthenticated = false]
export const putRequest = (url: string, data: any, header: object = {}) =>
  request({ url, method: "PUT", isAuthenticated: false, data, header });

// calls the [request] function with [url], [data], [method = 'PUT'] and [isAuthenticated = true]
export const putAuthenticatedRequest = (
  url: string,
  data: any,
  header: object = {}
) =>
  request({
    url,
    method: "PUT",
    isAuthenticated: true,
    data,
    header,
  });

// calls the [request] function with [url], [data], [method = 'PATCH'] and [isAuthenticated = false]
export const patchRequest = (url: string, data: any, header: object = {}) =>
  request({
    url,
    method: "PATCH",
    isAuthenticated: false,
    data,
    header,
  });

// calls the [request] function with [url], [data], [method = 'PATCH'] and [isAuthenticated = true]
export const patchAuthenticatedRequest = (
  url: string,
  data: any,
  header: object = {},
  notes: string = ""
) =>
  request({
    url,
    method: "PATCH",
    isAuthenticated: true,
    data,
    header,
    action: "edit",
    notes: notes,
  });

// calls the [request] function with [url], [data], [method = 'DELETE'] and [isAuthenticated = false]
export const deleteRequest = (url: string, data = null, header: object = {}) =>
  request({ url, method: "DELETE", isAuthenticated: false, data, header });

// calls the [request] function with [url], [data], [method = 'DELETE'] and [isAuthenticated = true]
export const deleteAuthenticatedRequest = (
  url: string,
  data = null,
  header: object = {},
  notes: string = ""
) =>
  request({
    url,
    method: "DELETE",
    isAuthenticated: true,
    data,
    header,
    action: "delete",
    notes: notes,
  });

export const fetchWrapper = async (
  url,
  options = {},
  isAuthenticated = true
) => {
  try {
    const state = store.getState();
    let userInfo = state.userLogin?.userInfo;
    const currentTabPermissions = state.currentTabPermissions;
    let accessToken = userInfo?.accessToken;

    // Handle token refresh if needed
    if (isAuthenticated && !accessToken) {
      if (!isRefreshing) {
        isRefreshing = true;
        try {
          const refreshResponse = await axios.post(
            `${process.env.REACT_APP_API_ENDPOINT_LEADS}/user/refresh-token`,
            {
              rToken: localStorage.getItem("refreshToken"),
            },
            { withCredentials: true }
          );

          const newAccessToken = refreshResponse.data.accessToken;
          const refreshToken = refreshResponse.data.refreshToken;

          store.dispatch({
            type: USER_LOGIN_SUCCESS,
            payload: {
              ...refreshResponse.data.savedUser,
              accessToken: newAccessToken,
            },
          });

          accessToken = newAccessToken;
          localStorage.setItem("refreshToken", refreshToken);
          onRefreshed(newAccessToken);
        } catch (refreshError) {
          console.error("Refresh token failed in fetchWrapper:", refreshError);
          localStorage.setItem("redirectPath", window.location.href);
          window.location.href = "/login";
          throw refreshError;
        } finally {
          isRefreshing = false;
        }
      } else {
        // Wait for ongoing token refresh
        await new Promise<void>((resolveSub) => {
          subscribeTokenRefresh((newToken: string) => {
            accessToken = newToken;
            resolveSub();
          });
        });
      }
    }

    // Prepare request options
    const requestOptions: any = {
      method: options.method,
      url,
      headers: {
        "Content-Type": "application/json",
        tab: currentTabPermissions?.tab,
        permissionid: currentTabPermissions?.permissionsId,
        eventid: currentTabPermissions?.permissionsEventId,
        ...options.headers,
      },
    };

    // Add body if applicable
    if (
      ["POST", "PUT", "PATCH", "DELETE"].includes(options.method) &&
      options.body
    ) {
      requestOptions.data = options.body;
    }

    // Add Authorization if required
    if (isAuthenticated && accessToken) {
      requestOptions.headers.Authorization = `Bearer ${accessToken}`;
    }

    const response = await axiosInstance(requestOptions);
    return response;
  } catch (err) {
    console.error(`Error during ${options.method} request to ${url}:`, err);
    throw err;
  }
};
